import { Link } from '@inertiajs/react';

import Button from '@/components/public/ui/Button';
import Icon from '@/components/public/ui/Icon';
import Section from '@/components/public/ui/Section';
import SectionHeader from '@/components/public/ui/SectionHeader';
import { trackUmamiEvent } from '@/lib/analytics';
import { eventDay, eventMonth, eventTime } from '@/lib/datetime';

type EventItem = {
  id: number;
  title: string;
  description: string | null;
  location: string | null;
  startsAt: string;
  endsAt: string | null;
  imageUrl: string | null;
};

type Props = {
  events: EventItem[];
};

function dayMonth(iso: string): { day: string; month: string } {
  return { day: eventDay(iso), month: eventMonth(iso) };
}

const UpcomingEvents = ({ events }: Props) => {
  return (
    <Section spacing="md">
      <SectionHeader
        eyebrow="Agenda"
        title="Próximos eventos"
        intro="Descubra o que está a acontecer na nossa freguesia."
        action={
          <Button
            variant="ghost"
            size="sm"
            href="/events"
            iconAfter="arrow_forward"
            onClick={() =>
              trackUmamiEvent('home_section_cta_click', {
                section: 'events',
                label: 'Agenda completa',
                target: '/events',
              })
            }
          >
            Agenda completa
          </Button>
        }
      />

      {events && events.length > 0 ? (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {events.map((event) => {
            const { day, month } = dayMonth(event.startsAt);
            return (
              <Link
                key={event.id}
                href="/events"
                onClick={() =>
                  trackUmamiEvent('home_event_click', {
                    event_id: event.id,
                    target: '/events',
                  })
                }
                className="surface-card group hover:border-line-strong relative flex gap-4 overflow-hidden p-5 transition-all duration-200 hover:shadow-md"
              >
                {/* Gold edge accent — slides in on hover */}
                <span className="bg-gold-leaf absolute inset-y-0 left-0 w-1 origin-top scale-y-0 transition-transform duration-200 group-hover:scale-y-100" />

                {/* Date block */}
                <div className="border-line bg-background flex h-16 w-16 flex-none flex-col items-center justify-center rounded-lg border">
                  <span className="font-headline text-primary text-2xl leading-none font-bold">
                    {day}
                  </span>
                  <span className="text-on-surface-light mt-0.5 text-[10px] font-bold tracking-wide uppercase">
                    {month}
                  </span>
                </div>

                {/* Content */}
                <div className="flex min-w-0 flex-1 flex-col">
                  <h3 className="font-headline text-on-surface text-base leading-snug font-semibold">
                    {event.title}
                  </h3>
                  <div className="text-on-surface-light mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                    <span className="inline-flex items-center gap-1">
                      <Icon name="schedule" size="sm" />
                      {eventTime(event.startsAt)}
                      {event.endsAt && ` – ${eventTime(event.endsAt)}`}
                    </span>
                    {event.location && (
                      <span className="inline-flex items-center gap-1">
                        <Icon name="location_on" size="sm" />
                        {event.location}
                      </span>
                    )}
                  </div>
                  {event.description && (
                    <p className="text-on-surface-light mt-2 line-clamp-2 text-sm">
                      {event.description}
                    </p>
                  )}
                  <span className="text-primary mt-auto inline-flex items-center gap-1 pt-3 text-sm font-semibold">
                    Ver detalhes
                    <Icon
                      name="arrow_forward"
                      size="sm"
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <p className="text-on-surface-light py-12 text-center">
          De momento não há eventos agendados. Volte em breve.
        </p>
      )}
    </Section>
  );
};

export default UpcomingEvents;
